import React, { memo, useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { Fade } from 'react-reveal';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

import FormBtn from '../../../components/elements/Buttons/FormBtn';
import GoBackBtn from '../../../components/elements/Buttons/GoBackBtn';
import { FormCard, FormCardInput } from '../../../components/FormCard';
import { PreviewRole } from '../../../components/modals/PreviewGuideRole';
import { Loader } from '../../../components/Spinner';
import SuccessDialog from '../../../components/SuccessDialog';
import { closeMessageUser, updateUser } from '../../../features/users/manageUsersSlice';
import { selectUserInfo } from '../../../features/users/userInfoSlice';
import { fetchWebsites, selectAll } from '../../../features/websites/websitesSlice';

/**
 * EDIT USER COMPONENT
 * @returns {JSX.Element}
 */
function EditUser() {
  const history = useHistory()
  const dispatch = useDispatch();

  /**
   * @type {{state: {user: User}}}
   */
  const {
    state: { user },
  } = useLocation()

  /**
   * @type {Website[]}
   */
  const websites = useSelector(selectAll)

  /**
   * @type {boolean}
   */
  const loadingWebsite = useSelector(state => state.websites.loading)

  /**
   * @type {boolean}
   */
  const loadingUser = useSelector(state => state.users.loading)

  /**
   * @type {boolean}
   */
  const showMessage = useSelector(state => state.users.showMessage)
  const userInfoContext = useSelector(selectUserInfo)
  const {
    register,
    handleSubmit,
    watch,
    setValue
    //formState: { errors }
  } = useForm();

  const [preview, setPreview] = useState(false)

  const roleSelected = watch('role')

  useEffect(() => {
    websites.forEach(website => {
      if (user.websites.some(w => w.id === website.id)) setValue(website.id, true)
    })
  }, [websites])

  useEffect(() => {
    setValue('role', user.admin ? 'Admin' : 'User')
    if (websites.length > 0) return
    dispatch(fetchWebsites({ company_id: userInfoContext?.user?.company_id }))
  }, [userInfoContext?.user?.company_id])

  const submitInfo = useCallback((userData) => {
    const admin_user = userData.role.toLowerCase() === 'admin' ? true : false
    delete userData.role

    /**
     * @type {{[website_id: string]: boolean}}
     */
    const allowance_websites = {}

    if (!admin_user) {
      for (const website of websites) {
        allowance_websites[website.id] = website.id in userData && userData[website.id]
      }
    }

    dispatch(updateUser({
      company_id: userInfoContext?.user?.company_id,
      requested_user_id: user.id,
      admin_user,
      allowance_websites: admin_user ? null : allowance_websites
    }))
  }, [userInfoContext?.user?.company_id, websites, user])

  const _previewRole = useCallback(() => {
    setPreview(true)
  }, [])

  const _closeDialog = useCallback(() => {
    dispatch(closeMessageUser())
    history.push('/account/users')
  }, [])

  return (
    <>
      <GoBackBtn />
      <SuccessDialog open={showMessage} close={_closeDialog} description="Vos informations ont bien été enregistrées." />
      <div className="max-w-xl mx-auto mt-40 flex items-center">
        <form className="w-full" onSubmit={handleSubmit(submitInfo)}>
          <FormCard center title={`Information sur ${user.name}`}>
            <div>
              <FormCardInput
                type="select"
                options={[{ label: "Administrateur", value: "Admin" }, { label: "Utilisateur", value: "User" }]}
                register={register}
                name="role"
              />
              <button
                title="Voir le guide des roles utilisateurs"
                type="button"
                className="flex items-center justify-center space-x-1 text-gray-600 transition-colors hover:underline hover:text-teal-800 px-4 py-2 rounded-full text-sm"
                onClick={_previewRole}
              >
                <span>Guide</span> <QuestionMarkCircleIcon className="h-5" />
              </button>
            </div>
            {roleSelected !== 'Admin' && <Fade bottom>
              <>
                <div className="divider text-general-blue font-semibold py-5">ACCÈS AUX MARQUES</div>
                {loadingWebsite && websites.length <= 0 && <Loader />}
                {websites.map((website, key) => {
                  return <label key={key} className="flex items-center justify-center space-x-2 cursor-pointer mb-2">
                    <input {...register(website.id)} type="checkbox" className="toggle toggle-accent peer" />
                    <span className="font-semibold text-gray-500 text-md peer-checked:text-teal-500">{website.title}</span>
                  </label>
                })}
              </>
            </Fade>}
            <FormBtn label="Enregistrer" disabled={loadingWebsite || loadingUser} />
          </FormCard>
        </form>
      </div>
      <PreviewRole open={preview} setOpen={setPreview} />
    </>
  )
}

export default memo(EditUser)